import React, { useEffect, useMemo, useRef, useState } from "react";
import { Col, OverlayTrigger, Popover } from "react-bootstrap";
import styles from "./Inputs.module.css";
import Langs, { Localize } from "../../app/lang/langs";

// Below this many options a search field is noise (the day-period pickers in
// ShiftFormModal have three), above it the list is worth filtering.
const SEARCH_THRESHOLD = 7

export function TransparentSelect(props) {

  const popoverRef = useRef()
  const buttonRef = useRef()
  const searchRef = useRef()

  const [showPopover, setShowPopover] = useState(false)
  const [searchText, setSearchText] = useState("")
  const [isMulti, setIsMulti] = useState(props.value.length > 1 ? true : false)
  const [search, setSearch] = useState('')

  // Derived from props + search instead of mirrored in state, so a refreshed
  // options list can no longer drop the active filter (or vice versa).
  const options = useMemo(() => {
    if (!props.options) return []
    if (search.trim() === '') return props.options
    return props.options.filter(option =>
      option.label.toLowerCase().includes(search.toLowerCase()))
  }, [props.options, search])

  const showSearch = props.options && props.options.length >= SEARCH_THRESHOLD

  let timer

  const handleKeyPress = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      setShowPopover(!showPopover)
      return
    }
    if (event.key === "Escape") {
      setShowPopover(false)
      return
    }
    if (event.key && event.key.length == 1) {
      let newSearchText = searchText + event.key
      if (props.options) {
        let foundOptions = props.options.filter((option) => {
          return option.label.toLowerCase().startsWith(newSearchText.toLocaleLowerCase())
        })
        if (foundOptions[0])
          document.getElementById(foundOptions[0].value)?.scrollIntoView({
            block: "center"
          })
      }
      setSearchText(newSearchText)
      if (timer) timer.invalidate()
      timer = setTimeout(() => {
        setSearchText("")
      }, 1000)
    }
  }

  const labelForValue = (value) => {
    for (const key in props.options) {
      let option = props.options[key]
      if (value == option.value) return option.label
    }
    return null
  }

  /**
   * Trigger text. With several values selected it names the first one and counts
   * the rest, which reads better than a bare "N selected" that hid every name.
   */
  const getSelectedValue = () => {
    let selectedId = props.value
    if (isMulti && props.value && props.value.length) {
      if (props.value.length === 1) {
        selectedId = props.value[0]
      } else {
        const first = labelForValue(props.value[0])
        if (!first) {
          return <Langs str="ITEMS_SELECTED" params={{ COUNT: props.value.length }} />
        }
        return (
          <>
            <span className={styles.transparentSelectValue}>{first}</span>
            <span className={styles.transparentSelectCount}>+{props.value.length - 1}</span>
          </>
        )
      }
    }
    const label = labelForValue(selectedId)
    if (label) return <span className={styles.transparentSelectValue}>{label}</span>
    return <span className={styles.transparentSelectPlaceholder}><Langs str="Select" /></span>
  }

  function multiSelectHandler(value) {
    let selectedList = []
    if (isMulti) {
      let addValue = true
      if (props.value) {
        selectedList = props.value.filter(v => {
          if (v == value) {
            addValue = false
            return false
          }
          return true
        })
      }
      if (addValue) {
        selectedList.push(value)
      }
      if (!props.max || (props.max >= selectedList.length && props.min <= selectedList.length)) {
        props.onChange(selectedList, value)
      }
    } else {
      setShowPopover(false)
      selectedList = [value]
      props.onChange(selectedList, value)
    }
  }

  const isChecked = (option) => {
    if (props.value) {
      if (props.value.filter(v => v == option.value).length)
        return true
    }
    return false
  }

  const handleMultiClick = () => {
    if (isMulti && props.value.length > 1) {
      props.onChange([props.value[0]])
    }
    setIsMulti(!isMulti)
  }

  // Reset the filter when the popover closes so it never reopens pre-filtered,
  // and focus the search field when it opens with one.
  useEffect(() => {
    if (!showPopover) {
      setSearch('')
      setSearchText('')
    } else if (showSearch) {
      const id = setTimeout(() => searchRef.current && searchRef.current.focus(), 0)
      return () => clearTimeout(id)
    }
  }, [showPopover, showSearch])

  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target) &&
        !buttonRef.current.contains(event.target)
      ) {
        setShowPopover(false)
      }
    }
    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [popoverRef])

  let uniqueCatgories = []

  return (
    <OverlayTrigger
      show={showPopover}
      key={"bottom"}
      placement={"bottom-start"}
      overlay={
        <Popover id="popover-positioned-bottom" className={styles.popover}>
          <Popover.Body
            ref={popoverRef}
            className={styles.transparentSelectPopover}
          >
            <div className={styles.selectPopoverHead}>
              {/* Deliberately a div, not a label: a label forwards the click to the
                  checkbox, which bubbles back and toggles the mode twice. */}
              <div className={styles.multiSelectOption} onClick={handleMultiClick}>
                <input type="checkbox"
                  checked={isMulti}
                  onChange={() => { }} />
                <span><Langs str={"MULTI_SELECTION"} /></span>
              </div>

              {showSearch && (
                <div className={styles.searchBarTeams}>
                  <i className={`pi pi-search ${styles.searchBarTeamsIcon}`} aria-hidden="true" />
                  <input
                    ref={searchRef}
                    className={styles.searchBarTeamsInput}
                    value={search}
                    placeholder={Localize("SEARCH")}
                    onChange={(e) => { setSearch(e.target.value) }}
                  />
                </div>
              )}
            </div>

            <div className={styles.selectPopoverList}>
              {options.length === 0 && (
                <div className={styles.selectPopoverEmpty}>
                  <Langs str="NO_RESULTS" />
                </div>
              )}
              {options.map((option) => {
                let displayCategory = false
                if (option.category && !uniqueCatgories.includes(option.category)) {
                  displayCategory = true
                  uniqueCatgories.push(option.category)
                }
                const checked = isChecked(option)
                return (
                  <Col key={"_" + option.value} className="m-0">
                    {displayCategory &&
                      <div className={styles.selectPopoverCategory}>
                        <Langs str={option.category} />
                      </div>}
                    {isMulti ?
                      <div
                        className={`${styles.customSelectOption} ${checked ? styles.customSelectOptionChecked : ""}`}
                        style={{
                          backgroundColor:
                            (searchText && option.label.toLowerCase().startsWith(searchText.toLocaleLowerCase()) ?
                              "rgba(255, 144, 21, 0.14)"
                              : undefined)
                        }}
                        id={option.value}
                        onClick={() => multiSelectHandler(option.value)}>
                        {checked || !props.max || props.max > props.value.length ?
                          <input type="checkbox"
                            checked={checked}
                            onChange={() => {
                            }} />
                          :
                          <div className={styles.customSelectOptionSpacer}></div>
                        }
                        <label>
                          {option.label}
                        </label>
                      </div>
                      :
                      <button
                        id={option.value}
                        style={{
                          backgroundColor:
                            (searchText && option.label.toLowerCase().startsWith(searchText.toLocaleLowerCase()) ?
                              "rgba(255, 144, 21, 0.14)"
                              : undefined)
                        }}
                        className={`${styles.optionButton} ${checked ? styles.optionButtonSelected : ""}`}
                        onClick={() => multiSelectHandler(option.value)}>
                        <span>{option.label}</span>
                        {checked && <i className="pi pi-check" aria-hidden="true" />}
                      </button>
                    }
                  </Col>
                )
              })}
            </div>
          </Popover.Body>
        </Popover>
      }
    >
      <button
        type="button"
        className={`${styles.transparentSelect} ${showPopover ? styles.transparentSelectOpen : ""}`}
        ref={buttonRef}
        aria-haspopup="listbox"
        aria-expanded={showPopover}
        onKeyDown={handleKeyPress}
        onClick={() => setShowPopover(!showPopover)}
      >
        {props.icon && <i className={`pi ${props.icon} ${styles.transparentSelectIcon}`} aria-hidden="true" />}
        {getSelectedValue()}
        <i className={`pi pi-chevron-down ${styles.transparentSelectChevron}`} aria-hidden="true" />
      </button>
    </OverlayTrigger>
  )
}
